import React, { useState } from 'react';
import { useI18n } from '../i18n';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { trackFaqToggle } from '../lib/analytics';

export const Faq: React.FC = () => {
  const { t } = useI18n();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    const isCurrentlyOpen = openIndex === index;
    const nextIndex = isCurrentlyOpen ? null : index;
    setOpenIndex(nextIndex);
    const item = t.faq.items[index];
    if (item) {
      trackFaqToggle(index, item.question, isCurrentlyOpen ? 'close' : 'open');
    }
  };

  return (
    <section id="faq" className="py-20 md:py-28 relative bg-slate-100/70 dark:bg-navy-950/50 border-y border-slate-200 dark:border-navy-800/80 transition-colors">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>{t.faq.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.faq.title}
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-300">
            {t.faq.subtitle}
          </p>
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {t.faq.items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className="glow-card rounded-xl overflow-hidden border-slate-200 dark:border-navy-700/80 transition-all"
              >
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="w-full py-4 px-6 text-left flex items-center justify-between gap-4 focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gold-600 dark:text-gold-400 shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isOpen && (
                  <div className="px-6 pb-5 pt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200 dark:border-navy-800/60 animate-fade-in font-normal">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
